import type {
  AttentionKind,
  AttentionRow,
  SubtaskParentRef,
  WorkspaceTaskSummary,
} from '@/features/tasks';
import { outcomesFor, type Outcome } from '@/features/tasks';

/**
 * Your turn (spec 152): everything in the workspace that waits for a person,
 * in one queue. Pure, so the order a person reads is tested without
 * rendering.
 *
 * - Severity first: what went wrong, then decisions, then subtasks to start.
 * - Then the longest wait.
 * - A parent's subtasks sit together under it, the group placed by its worst,
 *   longest-waiting row, with the parent's own row (when it has one) first.
 * - At most `QUEUE_CAP` rows are shown, whole groups where possible; the
 *   rest are counted for "Show all N in Tasks".
 */

export type QueueSeverity = 'wrong' | 'decision' | 'to-start';

export type QueueRow = AttentionRow & { severity: QueueSeverity };

export interface QueueGroup {
  /** The parent's id, or the row's own id for a task with no parent. */
  key: string;
  /** The parent to name above the rows; `null` for a task on its own. */
  parent: SubtaskParentRef | null;
  rows: QueueRow[];
}

export interface YourTurn {
  /** Every group, in reading order. */
  groups: QueueGroup[];
  /** The groups the page shows: at most `QUEUE_CAP` rows. */
  shown: QueueGroup[];
  total: number;
  breakdown: { wrong: number; decisions: number; toStart: number };
  /** The longest wait's start, for "oldest 1 day"; `null` when empty. */
  oldestEnteredAt: string | null;
  /** Rows not shown, for "Show all N in Tasks". */
  overflow: number;
}

/**
 * What the queue is called, and what its count says, to the person reading
 * it. A reader (`useCanWork()` false) cannot act on any of it, so for them it
 * is not their turn: the same rows are what needs attention, and they wait on
 * the workspace's members (spec 193).
 */
export function queueWords(canWork: boolean): {
  heading: string;
  count: (total: number) => string;
  allClear: string;
} {
  return canWork
    ? {
        heading: 'Your turn',
        count: (total) => (total === 1 ? 'thing needs you' : 'things need you'),
        allClear: 'Nothing waits for you',
      }
    : {
        heading: 'Needs attention',
        count: (total) =>
          total === 1
            ? 'thing waits on its members'
            : 'things wait on its members',
        allClear: 'Nothing waits on its members',
      };
}

/** A first screen's worth. More belongs on the Tasks page (FR-011). */
export const QUEUE_CAP = 20;

const SEVERITY: Record<AttentionKind, QueueSeverity> = {
  'failed-run': 'wrong',
  'failed-workdir': 'wrong',
  orphaned: 'wrong',
  stopped: 'wrong',
  'spend-limit': 'wrong',
  'needs-account': 'wrong',
  'human-gate': 'decision',
  branching: 'decision',
  'subtasks-to-start': 'to-start',
};

const RANK: Record<QueueSeverity, number> = {
  wrong: 0,
  decision: 1,
  'to-start': 2,
};

export function yourTurn(
  attention: WorkspaceTaskSummary['attention'],
  cap: number = QUEUE_CAP,
): YourTurn {
  const rows: QueueRow[] = [
    ...attention.needsAttention,
    ...attention.waiting,
    ...attention.toStart,
  ].map((each) => ({ ...each, severity: SEVERITY[each.kind] }));

  const byKey = new Map<string, QueueRow[]>();
  for (const each of rows) {
    const key = each.parent?.taskId ?? each.taskId;
    const members = byKey.get(key);
    if (members) members.push(each);
    else byKey.set(key, [each]);
  }

  const groups: (QueueGroup & { worst: QueueRow })[] = [];
  for (const [key, members] of byKey) {
    const ordered = [...members].sort(compareRows);
    const worst = ordered[0];
    if (!worst) continue;
    // The parent's own row leads its group; its subtasks follow in order.
    const own = ordered.filter((each) => each.taskId === key);
    const subtasks = ordered.filter((each) => each.taskId !== key);
    const parent = subtasks.length > 0 ? (subtasks[0]?.parent ?? null) : null;
    groups.push({ key, parent, rows: [...own, ...subtasks], worst });
  }
  groups.sort((a, b) => compareRows(a.worst, b.worst));
  const ordered: QueueGroup[] = groups.map(
    ({ key, parent, rows: members }) => ({
      key,
      parent,
      rows: members,
    }),
  );

  const shown: QueueGroup[] = [];
  let count = 0;
  for (const group of ordered) {
    if (count + group.rows.length <= cap) {
      shown.push(group);
      count += group.rows.length;
      continue;
    }
    // Only a group that alone is over the cap is cut; otherwise the queue
    // stops before it rather than skipping ahead to smaller groups.
    if (shown.length === 0) {
      shown.push({ ...group, rows: group.rows.slice(0, cap) });
      count = cap;
    }
    break;
  }

  const oldest = [...rows].sort(compareWait)[0];
  return {
    groups: ordered,
    shown,
    total: rows.length,
    breakdown: {
      wrong: rows.filter((each) => each.severity === 'wrong').length,
      decisions: rows.filter((each) => each.severity === 'decision').length,
      toStart: rows.filter((each) => each.severity === 'to-start').length,
    },
    oldestEnteredAt: oldest?.enteredAt ?? null,
    overflow: rows.length - count,
  };
}

/** Worst first, then the longest wait. */
function compareRows(a: QueueRow, b: QueueRow): number {
  return RANK[a.severity] - RANK[b.severity] || compareWait(a, b);
}

/** Longest wait first. Times that will not parse sort last, together. */
function compareWait(a: AttentionRow, b: AttentionRow): number {
  const at = Date.parse(a.enteredAt);
  const bt = Date.parse(b.enteredAt);
  const aBad = Number.isNaN(at);
  const bBad = Number.isNaN(bt);
  if (aBad || bBad) return Number(aBad) - Number(bBad);
  return at - bt;
}

/**
 * One action for a parent's waiting subtasks (spec 154 US4): when every row
 * in the group is a decision at the same step, with the same choices, its
 * header offers the forward one ("Sign off all 3"). `null` otherwise.
 *
 * Read at call time only: `outcomesFor` comes from the tasks package, which
 * sits on an import cycle with this one (spec 152).
 */
export interface QueueBatch {
  rows: QueueRow[];
  /** The forward choice: the first, as each row shows it first. */
  outcome: Outcome;
  label: string;
}

export function batchOf(group: QueueGroup): QueueBatch | null {
  const [first, ...others] = group.rows;
  if (!group.parent || !first || others.length === 0) return null;
  const decisionAt = (row: QueueRow) =>
    row.severity === 'decision' && row.hasBoard && row.step !== undefined;
  if (!group.rows.every(decisionAt)) return null;
  const keysOf = (row: QueueRow) =>
    outcomesFor(row.step, row.steps)
      .map((outcome) => outcome.key)
      .join(' ');
  const keys = keysOf(first);
  const same = others.every(
    (row) =>
      row.workflowKey === first.workflowKey &&
      row.step?.stepId === first.step?.stepId &&
      keysOf(row) === keys,
  );
  const [outcome] = outcomesFor(first.step, first.steps);
  if (!same || !outcome) return null;
  return {
    rows: group.rows,
    outcome,
    label: `${outcome.label} all ${String(group.rows.length)}`,
  };
}

/**
 * The rework: the one choice leading to an earlier step on the board (spec
 * 154 R4). `null` when there is none or more than one, so R never guesses.
 */
export function reworkOf(row: QueueRow): Outcome | null {
  const order = row.steps.map((step) => step.stepId);
  const here = order.indexOf(row.step?.stepId ?? '');
  if (here < 0) return null;
  const back = outcomesFor(row.step, row.steps).filter((outcome) => {
    const there = order.indexOf(outcome.to);
    return there >= 0 && there < here;
  });
  return back.length === 1 ? (back[0] ?? null) : null;
}
